/**
 * The index behind the sidebar's chart search, and the scroll that lands on a chosen chart.
 *
 * The index is read from the DOM rather than kept in a store. The question the feature asks is
 * literally "which charts are on screen right now, and in what order" — the DOM is that answer. A
 * store would answer "which charts registered and have not yet unregistered", which is the same
 * thing only if every registration path is balanced; the maneuver timeseries charts are built
 * imperatively by d3 in a function that tears down and rebuilds on every group fetch, view swap,
 * resize and zoom, so it would not be. Split view makes it worse: two report pages mount at once.
 *
 * Lazy mounting is fine: anchors sit on the always-present wrapper (PerformanceChartViewportSlot,
 * .timeseries-chart-wrapper), so charts below the fold are findable and mount once scrolled to.
 *
 * Charts opt in by carrying `data-chart-search-title`, plus an optional
 * `data-chart-search-detail` for the group/context line.
 */

import { getEffectiveScale } from "./effectiveScale";
import { compileChannelSearch, matchesChannelSearch, type ChannelSearchQuery } from "./channelSearchMatch";

/** How many rows the sidebar shows. Load-bearing for the layout AND the keyboard model. */
export const CHART_SEARCH_VISIBLE_RESULTS = 3;

export const CHART_SEARCH_TITLE_ATTR = "data-chart-search-title";
export const CHART_SEARCH_DETAIL_ATTR = "data-chart-search-detail";

export interface ChartSearchTarget {
  title: string;
  /** Group/context line, "" when the surface has none. */
  detail: string;
  el: Element;
  scroller: HTMLElement;
}

export interface ChartSearchRow {
  title: string;
  detail: string;
  /** First occurrence in document order — the one a pick scrolls to. */
  target: ChartSearchTarget;
  /** How many charts this row stands for (>1 on fleet pages, where sources repeat a channel). */
  count: number;
}

export interface ChartSearchResults {
  rows: ChartSearchRow[];
  /** Distinct rows that matched, before the display cap — so "+N more" cannot lie. */
  total: number;
}

const isScrollable = (el: Element): boolean => {
  const style = window.getComputedStyle(el);
  const oy = style.overflowY;
  return oy === "auto" || oy === "scroll";
};

/** A declared-scrollable element that has nothing to scroll is not the scroll owner. */
const overflows = (el: Element): boolean => el.scrollHeight > el.clientHeight + 1;

/**
 * Nearest scrollable ancestor, falling back to #main-content.
 *
 * Generalises the resolver in components/maneuvers/standard/Scatter.tsx instead of special-casing
 * per page: walking up on computed overflow lands on .performance-charts-scroll-inner for
 * performance (the outer -container is overflow:hidden) and falls through to #main-content for the
 * maneuver views, with no page-specific knowledge here.
 */
export function findScrollParent(el: Element): HTMLElement {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (isScrollable(node) && overflows(node)) {return node;}
  }

  // Nothing between the chart and the page scrolls. Mirror the cascade the maneuver timeseries
  // already uses (#main-content -> body -> documentElement), picking the first that actually
  // overflows: writing scrollTop on a non-scrolling element is a silent no-op.
  const main = document.getElementById("main-content");
  if (main && overflows(main)) {return main;}
  const scrolling = document.scrollingElement as HTMLElement | null;
  if (scrolling && overflows(scrolling)) {return scrolling;}
  return main ?? scrolling ?? document.documentElement;
}

/**
 * Every opted-in chart on the page, in document order.
 *
 * Split-view scoping: the sidebar drives the left panel, which precedes the right in document
 * order, so we keep only targets sharing the first match's scroll container. That is deterministic
 * and needs no panel-identity plumbing.
 */
export function collectChartSearchTargets(root: ParentNode = document): ChartSearchTarget[] {
  const els = Array.from(root.querySelectorAll(`[${CHART_SEARCH_TITLE_ATTR}]`));
  if (els.length === 0) {return [];}

  const all = els.map((el) => ({
    title: (el.getAttribute(CHART_SEARCH_TITLE_ATTR) ?? "").trim(),
    detail: (el.getAttribute(CHART_SEARCH_DETAIL_ATTR) ?? "").trim(),
    el,
    scroller: findScrollParent(el),
  }));

  const primary = all[0].scroller;
  return all.filter((t) => t.title.length > 0 && t.scroller === primary);
}

const fold = (s: string): string => String(s ?? "").trim().toLowerCase().replace(/\./g, "_");

/**
 * Rank a matched row. LOWER is better.
 *
 * Only three rows are ever shown, so they have to be the *best* three rather than the first three
 * in page order: exact title > title starts with the query > earliest hit position > shorter title
 * (less padding around the hit). Callers break remaining ties with document order, so equal scores
 * still read top-to-bottom the way the page does.
 */
export function scoreChartSearchMatch(title: string, query: string): number {
  const needle = fold(query).replace(/\*/g, "");
  const hay = fold(title);
  if (!needle) {return 3_000_000 + Math.min(hay.length, 999);}

  const idx = hay.indexOf(needle);
  let rank: number;
  if (hay === needle) {rank = 0;}
  else if (idx === 0) {rank = 1;}
  else if (idx > 0) {rank = 2;}
  else {rank = 3;}

  const position = idx >= 0 ? Math.min(idx, 999) : 999;
  return rank * 1_000_000 + position * 1_000 + Math.min(hay.length, 999);
}

/**
 * Match, collapse duplicates, rank, then cap.
 *
 * Duplicates are collapsed BEFORE the cap because several charts can share a title — on fleet pages
 * the same channel appears once per source, so three identical rows would otherwise consume the
 * whole budget. `detail` is part of the key: the same Y channel in two Performance groups really is
 * two different charts.
 */
export function buildChartSearchResults(
  targets: readonly ChartSearchTarget[],
  query: string | ChannelSearchQuery,
  limit: number = CHART_SEARCH_VISIBLE_RESULTS
): ChartSearchResults {
  const compiled = typeof query === "string" ? compileChannelSearch(query) : query;
  if (compiled.isEmpty) {return { rows: [], total: 0 };}
  const raw = typeof query === "string" ? query : "";

  const byKey = new Map<string, ChartSearchRow>();
  const matched: ChartSearchRow[] = [];

  for (const target of targets) {
    if (!matchesChannelSearch([target.title, target.detail], compiled)) {continue;}
    const key = `${target.title}\u0000${target.detail}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    const row = { title: target.title, detail: target.detail, target, count: 1 };
    byKey.set(key, row);
    matched.push(row);
  }

  const rows = matched.map((row, i) => ({ row, i }));
  rows.sort((a, b) => {
    const d = scoreChartSearchMatch(a.row.title, raw) - scoreChartSearchMatch(b.row.title, raw);
    return d !== 0 ? d : a.i - b.i; // document order breaks ties
  });

  return { rows: rows.slice(0, limit).map((r) => r.row), total: rows.length };
}

export interface ChartScrollMathArgs {
  /** Layout px. */
  currentScrollTop: number;
  /** Painted px — getBoundingClientRect().top of the chart. */
  targetPaintedTop: number;
  /** Painted px — getBoundingClientRect().top of the scroller. */
  scrollerPaintedTop: number;
  /** Layout px. */
  scrollerClientHeight: number;
  /** Layout px. */
  scrollerScrollHeight: number;
  /** Painted/layout scale of the page. */
  scale: number;
  /** Layout px of breathing room above the chart. */
  marginPx: number;
}

/**
 * Where to put scrollTop so a chart lands just below the top of its scroller.
 *
 * The load-bearing fact: scrollTop / clientHeight / scrollHeight are LAYOUT pixels and ignore an
 * ancestor transform, while getBoundingClientRect() returns PAINTED pixels and does not. Mixing the
 * two is exactly what makes scrollIntoView and the naive `rect.top - scrollerRect.top + scrollTop`
 * formula wrong on report pages, which run under a transform:scale()/zoom media-container scaler.
 * Dividing the painted delta by the scale is the whole trick.
 *
 * This is only valid while the transform sits on an ancestor of BOTH the scroller and the chart
 * (it lives on #media-container). A transform introduced *between* them would break it.
 */
export function computeChartScrollTop(a: ChartScrollMathArgs): number {
  const s = Number.isFinite(a.scale) && a.scale > 0 ? a.scale : 1;
  const deltaLayout = (a.targetPaintedTop - a.scrollerPaintedTop) / s;
  const raw = a.currentScrollTop + deltaLayout - a.marginPx;
  const max = Math.max(0, a.scrollerScrollHeight - a.scrollerClientHeight);
  if (!Number.isFinite(raw)) {return a.currentScrollTop;}
  return Math.min(Math.max(raw, 0), max);
}

const FLASH_CLASS = "chart-search-flash";
/** Must match the .chart-search-flash animation duration in Styles.css. */
const FLASH_MS = 2400;

/** Brief outline on arrival — the page moves with no animation, so otherwise you cannot tell where you landed. */
function flashChart(el: Element): void {
  if (!(el instanceof HTMLElement)) {return;} // SVG <g> targets simply do not flash
  el.classList.remove(FLASH_CLASS);
  void el.offsetWidth; // restart the animation
  el.classList.add(FLASH_CLASS);
  window.setTimeout(() => el.classList.remove(FLASH_CLASS), FLASH_MS);
}

/**
 * Scroll a target into view.
 *
 * Instant, not smooth: smooth scrolling would drag the viewport across every intervening chart,
 * firing the maneuver timeseries IntersectionObserver for each one and triggering a full d3 draw
 * per chart — a visible stall on a 20-chart page, purely for cosmetics.
 *
 * The double-rAF second pass is a guard, not the mechanism (same idiom as
 * performanceChartsScrollRestore): the grouped timeseries path can change scrollHeight underneath
 * us right after the first write. Measure -> compute -> clamp -> write, on both passes.
 */
export function scrollToChartSearchTarget(target: ChartSearchTarget, marginPx = 16): void {
  const apply = () => {
    const sc = target.scroller;
    if (!sc || !target.el.isConnected) {return;}
    sc.scrollTop = computeChartScrollTop({
      currentScrollTop: sc.scrollTop,
      targetPaintedTop: target.el.getBoundingClientRect().top,
      scrollerPaintedTop: sc.getBoundingClientRect().top,
      scrollerClientHeight: sc.clientHeight,
      scrollerScrollHeight: sc.scrollHeight,
      scale: getEffectiveScale(sc),
      marginPx,
    });
  };

  apply();
  requestAnimationFrame(() => requestAnimationFrame(apply));
  flashChart(target.el);
}
